import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, User } from 'lucide-react';
import { z } from 'zod';
import { LanguageSchema, PasswordSchema } from '@forest/contracts';
import type { UserPublic, Language } from '@forest/contracts';
import { api, apiError } from '@/lib/api-client';
import { useAuthStore } from '@/stores/sessionStore';
import { useUiStore } from '@/stores/uiStore';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { useAllPrimarii, useOcoaleSilvice, useEntitatiSilvice } from '@/features/regions/useRegions';

// ---------- Schemas ----------

const PersonalInfoSchema = z.object({
  fullName: z.string().min(2).max(100),
  email: z.string().email().or(z.literal('')).optional(),
  preferredLanguage: LanguageSchema,
});
type PersonalInfoForm = z.infer<typeof PersonalInfoSchema>;

const DefaultMetadataSchema = z.object({
  defaultRaion: z.string().max(100).optional().default(''),
  defaultPrimarie: z.string().max(100).optional().default(''),
  defaultUcf: z.string().max(50).optional().default(''),
  defaultOcolSilvic: z.string().max(100).optional().default(''),
});
type DefaultMetadataForm = z.infer<typeof DefaultMetadataSchema>;

const ChangePasswordFormSchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: PasswordSchema,
    confirmPassword: z.string().min(1),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'passwordMismatch',
  });
type ChangePasswordForm = z.infer<typeof ChangePasswordFormSchema>;

// ---------- Component ----------

export function ProfilePage() {
  const { t, i18n } = useTranslation(['auth', 'common']);
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const pushToast = useUiStore((s) => s.pushToast);
  const setLanguage = useUiStore((s) => s.setLanguage);
  const qc = useQueryClient();

  // ---------- Fetch full profile ----------
  const { data: profile } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get('me').json<UserPublic>(),
    staleTime: 60_000,
  });

  // ---------- Section 1b: Default Metadata ----------
  const {
    register: regMetadata,
    handleSubmit: submitMetadata,
    reset: resetMetadata,
  } = useForm<DefaultMetadataForm>({
    resolver: zodResolver(DefaultMetadataSchema),
    defaultValues: {
      defaultRaion: profile?.defaultRaion ?? '',
      defaultPrimarie: profile?.defaultPrimarie ?? '',
      defaultUcf: profile?.defaultUcf ?? '',
      defaultOcolSilvic: profile?.defaultOcolSilvic ?? '',
    },
  });

  useEffect(() => {
    if (profile) {
      resetMetadata({
        defaultRaion: profile.defaultRaion ?? '',
        defaultPrimarie: profile.defaultPrimarie ?? '',
        defaultUcf: profile.defaultUcf ?? '',
        defaultOcolSilvic: profile.defaultOcolSilvic ?? '',
      });
    }
  }, [profile, resetMetadata]);

  const onSaveMetadata = (data: DefaultMetadataForm) => {
    updateProfileMut.mutate({
      defaultRaion: data.defaultRaion || null,
      defaultPrimarie: data.defaultPrimarie || null,
      defaultUcf: data.defaultUcf || null,
      defaultOcolSilvic: data.defaultOcolSilvic || null,
    });
  };

  // ---------- Cascading options for metadata dropdowns ----------
  // Multi-value entitate silvica (2026-04-30): list[0] is the user's default.
  const assignedEntity = profile?.assignedEntitatiSilvice?.[0];
  const { data: cascadingOptions } = useQuery({
    queryKey: ['me', 'cascading-options', assignedEntity],
    queryFn: () =>
      api
        .get('me/cascading-options', { searchParams: { entitateSilvica: assignedEntity! } })
        .json<{
          raioane: string[];
          primarii: string[];
          ocoaleSilvice: string[];
          ucfList: string[];
          byEntity?: Record<
            string,
            { raioane: string[]; primarii: string[]; ocoaleSilvice: string[]; ucfList: string[] }
          >;
        }>(),
    enabled: !!assignedEntity,
    staleTime: 5 * 60_000,
  });

  // Reference-table fallback so users without an assigned entity (e.g.
  // SUPER_ADMIN, CONTROLOR before any data lands) still get dropdowns
  // — mirrors the mobile profile picker behaviour.
  const allPrimariiQ = useAllPrimarii();
  const allOcoaleQ = useOcoaleSilvice();
  const allEntitatiQ = useEntitatiSilvice();

  // Resolve the user's assigned entity name → cod via the reference
  // table so the ocol filter (which keys on cod) still works.
  const norm = (s: string | null | undefined) =>
    (s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
  const assignedEntityCod = (() => {
    if (!assignedEntity) return '';
    const target = norm(assignedEntity);
    const hit = (allEntitatiQ.data?.items ?? []).find(
      (e) => norm(e.name) === target || norm(e.cod) === target,
    );
    return hit?.cod ?? '';
  })();

  // Cascade-first option builders. When the user has an assigned
  // entity, prefer the cascade (parcels for that entity) — that's the
  // tightest, most relevant set. Otherwise fall back to the regions
  // reference table so the dropdown is never empty.
  const raionOptions = (() => {
    if (cascadingOptions?.raioane?.length) return cascadingOptions.raioane;
    const seen = new Set<string>();
    for (const p of allPrimariiQ.data?.items ?? []) {
      if (p?.raion) seen.add(p.raion);
    }
    return [...seen].sort((a, b) => a.localeCompare(b, 'ro'));
  })();
  const primarieOptions = (() => {
    if (cascadingOptions?.primarii?.length) return cascadingOptions.primarii;
    return (allPrimariiQ.data?.items ?? [])
      .map((p) => p.name)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'ro'));
  })();
  const ocolOptions = (() => {
    if (cascadingOptions?.ocoaleSilvice?.length) return cascadingOptions.ocoaleSilvice;
    // No usable name on OcolSilvic; the ref table only has `cod`.
    // Filter by entity cod when known so the list stays sensible.
    const all = allOcoaleQ.data?.items ?? [];
    const filtered = assignedEntityCod
      ? all.filter((o) => o.entitateSilvicaCod === assignedEntityCod)
      : all;
    return filtered.map((o) => o.cod).filter(Boolean).sort((a, b) => a.localeCompare(b, 'ro'));
  })();
  const ucfOptions = cascadingOptions?.ucfList ?? [];

  // ---------- Section 1: Personal Info ----------
  const {
    register: regPersonal,
    handleSubmit: submitPersonal,
    formState: { errors: personalErrors },
    reset: resetPersonal,
  } = useForm<PersonalInfoForm>({
    resolver: zodResolver(PersonalInfoSchema),
    defaultValues: {
      fullName: profile?.fullName ?? user?.fullName ?? '',
      email: profile?.email ?? '',
      preferredLanguage: profile?.preferredLanguage ?? user?.preferredLanguage ?? 'ro',
    },
  });

  useEffect(() => {
    if (profile) {
      resetPersonal({
        fullName: profile.fullName,
        email: profile.email ?? '',
        preferredLanguage: profile.preferredLanguage,
      });
    }
  }, [profile, resetPersonal]);

  const updateProfileMut = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.patch('me', { json: data }).json<UserPublic>(),
    onSuccess: (updated) => {
      setUser(updated);
      void qc.invalidateQueries({ queryKey: ['auth', 'session'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
      pushToast({ variant: 'success', message: t('profile.saveSuccess') });
    },
    onError: async (e) => {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    },
  });

  const onSavePersonal = (data: PersonalInfoForm) => {
    const payload: Record<string, unknown> = {
      fullName: data.fullName,
      preferredLanguage: data.preferredLanguage,
    };
    if (data.email) {
      payload.email = data.email;
    } else {
      payload.email = null;
    }
    // Sync UI language
    if (data.preferredLanguage !== i18n.language) {
      setLanguage(data.preferredLanguage);
      void i18n.changeLanguage(data.preferredLanguage);
    }
    updateProfileMut.mutate(payload);
  };

  // ---------- Section 3: Notifications ----------
  const onToggleNotifications = useCallback(
    (checked: boolean) => {
      updateProfileMut.mutate({ emailNotifications: checked });
    },
    [updateProfileMut],
  );

  // ---------- Section 4: Change Password ----------
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register: regPassword,
    handleSubmit: submitPassword,
    formState: { errors: passwordErrors },
    reset: resetPassword,
  } = useForm<ChangePasswordForm>({
    resolver: zodResolver(ChangePasswordFormSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const changePasswordMut = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      api.post('me/change-password', { json: data }),
    onSuccess: () => {
      pushToast({ variant: 'success', message: t('profile.passwordChanged') });
      resetPassword();
    },
    onError: async (e) => {
      const err = await apiError(e);
      const msg = err.message.toLowerCase().includes('incorrect')
        ? t('profile.wrongPassword')
        : err.message;
      pushToast({ variant: 'error', message: msg });
    },
  });

  const onChangePassword = (data: ChangePasswordForm) => {
    changePasswordMut.mutate({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
  };

  const passwordToggleBtn = (show: boolean, toggle: () => void) => (
    <button
      type="button"
      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-700"
      onClick={toggle}
      tabIndex={-1}
      aria-label={show ? t('register.hidePassword') : t('register.showPassword')}
    >
      {show ? (
        <EyeOff className="h-4 w-4 text-slate-400 dark:text-slate-500" />
      ) : (
        <Eye className="h-4 w-4 text-slate-400 dark:text-slate-500" />
      )}
    </button>
  );

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-forest-100 text-forest-700 dark:bg-forest-900/40 dark:text-forest-300">
          <User className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{t('profile.title')}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('profile.subtitle')}</p>
        </div>
      </div>

      {/* Section 1: Personal Info */}
      <Card>
        <CardBody>
          <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
            {t('profile.personalInfo')}
          </h2>
          <form onSubmit={submitPersonal(onSavePersonal)} noValidate>
            <FormField
              label={t('profile.name')}
              htmlFor="fullName"
              error={personalErrors.fullName?.message}
              required
            >
              <Input id="fullName" {...regPersonal('fullName')} />
            </FormField>

            <FormField label={t('profile.username')} htmlFor="username">
              <Input
                id="username"
                value={profile?.username ?? user?.username ?? ''}
                disabled
                className="bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
              />
            </FormField>

            <FormField
              label={t('profile.cod', { defaultValue: 'Cod autor' })}
              htmlFor="cod"
            >
              <Input
                id="cod"
                value={profile?.cod ?? user?.cod ?? ''}
                disabled
                placeholder="—"
                className="bg-slate-50 font-mono text-slate-500 dark:bg-slate-800 dark:text-slate-400"
              />
            </FormField>

            <FormField
              label={t('profile.email')}
              htmlFor="email"
              error={personalErrors.email?.message}
            >
              <Input id="email" type="email" {...regPersonal('email')} />
            </FormField>

            <FormField
              label={t('profile.language')}
              htmlFor="preferredLanguage"
            >
              <Select id="preferredLanguage" {...regPersonal('preferredLanguage')}>
                <option value="ro">Romana</option>
                <option value="ru">Русский</option>
                <option value="en">English</option>
              </Select>
            </FormField>

            <Button type="submit" loading={updateProfileMut.isPending} className="mt-1">
              {t('profile.save')}
            </Button>
          </form>
        </CardBody>
      </Card>

      {/* Section 1b: Default Metadata */}
      <Card>
        <CardBody>
          <h2 className="mb-1 text-lg font-semibold text-slate-800">
            {t('profile.defaultMetadata')}
          </h2>
          <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
            {t('profile.defaultMetadataHint')}
          </p>
          <form onSubmit={submitMetadata(onSaveMetadata)} noValidate>
            <FormField label={t('profile.defaultRaion')} htmlFor="defaultRaion">
              <Select id="defaultRaion" {...regMetadata('defaultRaion')}>
                <option value="">{t('profile.none')}</option>
                {raionOptions.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </Select>
            </FormField>

            <FormField label={t('profile.defaultPrimarie')} htmlFor="defaultPrimarie">
              <Select id="defaultPrimarie" {...regMetadata('defaultPrimarie')}>
                <option value="">{t('profile.none')}</option>
                {primarieOptions.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </Select>
            </FormField>

            <FormField label={t('profile.defaultUcf')} htmlFor="defaultUcf">
              {ucfOptions.length > 0 ? (
                <Select id="defaultUcf" {...regMetadata('defaultUcf')}>
                  <option value="">{t('profile.none')}</option>
                  {ucfOptions.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </Select>
              ) : (
                <Input id="defaultUcf" {...regMetadata('defaultUcf')} />
              )}
            </FormField>

            <FormField label={t('profile.defaultOcolSilvic')} htmlFor="defaultOcolSilvic">
              <Select id="defaultOcolSilvic" {...regMetadata('defaultOcolSilvic')}>
                <option value="">{t('profile.none')}</option>
                {ocolOptions.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </Select>
            </FormField>

            <Button type="submit" loading={updateProfileMut.isPending} className="mt-1">
              {t('profile.save')}
            </Button>
          </form>
        </CardBody>
      </Card>

      {/* Section 2: Forestry Entity (read-only, multi-value) */}
      <Card>
        <CardBody>
          <h2 className="mb-4 text-lg font-semibold text-slate-800">
            {t('profile.entitateSilvica')}
          </h2>
          {(() => {
            const list = profile?.assignedEntitatiSilvice ?? [];
            if (list.length === 0) {
              return (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-500/30 dark:bg-amber-500/10">
                  <p className="text-sm text-amber-800 dark:text-amber-200">{t('profile.entityNotAssigned')}</p>
                </div>
              );
            }
            return (
              <div className="flex flex-wrap gap-2">
                {list.map((e, i) => (
                  <span
                    key={`${e}-${i}`}
                    className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-800 dark:bg-slate-800 dark:text-slate-200"
                  >
                    {e}
                    {i === 0 && list.length >= 2 && (
                      <span className="rounded bg-forest-100 px-1.5 text-xs text-forest-700 dark:bg-forest-900/40 dark:text-forest-300">
                        {t('profile.entitateSilvicaPrimary')}
                      </span>
                    )}
                  </span>
                ))}
              </div>
            );
          })()}
        </CardBody>
      </Card>

      {/* Section 3: Notifications */}
      <Card>
        <CardBody>
          <h2 className="mb-4 text-lg font-semibold text-slate-800">
            {t('profile.notifications')}
          </h2>
          <label className="flex cursor-pointer items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={profile?.emailNotifications ?? false}
              onClick={() => onToggleNotifications(!(profile?.emailNotifications ?? false))}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-forest-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 ${
                profile?.emailNotifications ? 'bg-forest-600' : 'bg-slate-200 dark:bg-slate-700'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  profile?.emailNotifications ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
            <div>
              <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                {t('profile.emailNotifications')}
              </span>
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('profile.emailNotificationsHint')}</p>
            </div>
          </label>
        </CardBody>
      </Card>

      {/* Section 4: Change Password */}
      <Card>
        <CardBody>
          <h2 className="mb-4 text-lg font-semibold text-slate-800">
            {t('profile.changePassword')}
          </h2>
          <form onSubmit={submitPassword(onChangePassword)} noValidate>
            <FormField
              label={t('profile.currentPassword')}
              htmlFor="currentPassword"
              error={passwordErrors.currentPassword?.message}
              required
            >
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrent ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="pr-10"
                  {...regPassword('currentPassword')}
                />
                {passwordToggleBtn(showCurrent, () => setShowCurrent((v) => !v))}
              </div>
            </FormField>

            <FormField
              label={t('profile.newPassword')}
              htmlFor="newPassword"
              error={passwordErrors.newPassword?.message}
              hint={t('profile.passwordHint')}
              required
            >
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNew ? 'text' : 'password'}
                  autoComplete="new-password"
                  className="pr-10"
                  {...regPassword('newPassword')}
                />
                {passwordToggleBtn(showNew, () => setShowNew((v) => !v))}
              </div>
            </FormField>

            <FormField
              label={t('profile.confirmPassword')}
              htmlFor="confirmPassword"
              error={
                passwordErrors.confirmPassword?.message === 'passwordMismatch'
                  ? t('profile.passwordMismatch')
                  : passwordErrors.confirmPassword?.message
              }
              required
            >
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  autoComplete="new-password"
                  className="pr-10"
                  {...regPassword('confirmPassword')}
                />
                {passwordToggleBtn(showConfirm, () => setShowConfirm((v) => !v))}
              </div>
            </FormField>

            <Button type="submit" loading={changePasswordMut.isPending} className="mt-1">
              {t('profile.changePassword')}
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
